import { CartAddEvent, ThemeEvents } from '@theme/events';
import { fetchConfig } from '@theme/utilities';

/**
 * Cart rewards. cart-rewards.js
 *
 * 1. Keeps the free gift in sync with the cart: added once the cart total (gift excluded) reaches the threshold,
 *    removed below it, capped at quantity 1. The 0 € price comes from the matching automatic discount.
 * 2. Defines <cart-rewards>, which re-renders the progress bar amounts when the customer shops in a presentment
 *    currency other than the shop currency (Liquid has no FX rate, so the server renders at rate 1).
 *
 * Config is rendered in layout/theme.liquid as JSON in #cart-rewards-config.
 */

const SOURCE = 'cart-rewards';
const DEBOUNCE = 250;
const MAX_ACTIONS = 4;
const ACTION_WINDOW = 10000;

/**
 * @typedef {object} CartRewardsConfig
 * @property {boolean} [giftEnabled]
 * @property {number} [giftProductId]
 * @property {number} [giftVariantId]
 * @property {number} [threshold] - Gift threshold in shop currency cents
 * @property {string} [shopCurrency]
 * @property {boolean} [sync] - Whether the cart needs a reconciliation on page load
 */

/** @returns {CartRewardsConfig} */
function readConfig() {
  try {
    return JSON.parse(document.getElementById('cart-rewards-config')?.textContent || '{}');
  } catch {
    return {};
  }
}

const config = readConfig();

/**
 * Rate from the shop currency to the active presentment currency.
 * @param {string} [shopCurrency]
 * @returns {number}
 */
function currencyRate(shopCurrency = config.shopCurrency) {
  const currency = window.Shopify?.currency;
  const rate = parseFloat(currency?.rate);

  if (!currency?.active || !shopCurrency || currency.active === shopCurrency || !rate) return 1;

  return rate;
}

/** @returns {string[]} */
function cartSectionIds() {
  const ids = new Set();

  document.querySelectorAll('cart-items-component').forEach((element) => {
    if (element instanceof HTMLElement && element.dataset.sectionId) ids.add(element.dataset.sectionId);
  });

  return [...ids];
}

/**
 * Keeps the free gift line in sync with the cart.
 */
class GiftSync {
  #timer = 0;
  #running = false;
  #queued = false;
  #disabled = false;
  /** @type {number[]} */
  #actions = [];

  schedule = () => {
    window.clearTimeout(this.#timer);
    this.#timer = window.setTimeout(this.#run, DEBOUNCE);
  };

  #run = async () => {
    if (this.#disabled) return;

    if (this.#running) {
      this.#queued = true;
      return;
    }

    this.#running = true;

    try {
      await this.#reconcile();
    } catch (error) {
      console.error(error);
    } finally {
      this.#running = false;

      if (this.#queued) {
        this.#queued = false;
        this.schedule();
      }
    }
  };

  async #reconcile() {
    const response = await fetch(`${Theme.routes.cart_url}.js`, {
      headers: { Accept: 'application/json' },
      cache: 'no-store',
    });
    if (!response.ok) return;

    const cart = await response.json();
    /** @type {any[]} */
    const giftLines = cart.items.filter((/** @type {any} */ item) => item.product_id === config.giftProductId);
    const giftTotal = giftLines.reduce((sum, item) => sum + item.final_line_price, 0);
    const giftQuantity = giftLines.reduce((sum, item) => sum + item.quantity, 0);
    const eligible = cart.total_price - giftTotal;
    const threshold = Math.round((config.threshold ?? 0) * currencyRate());
    const qualifies = cart.items.length > giftLines.length && eligible >= threshold;

    if (qualifies && giftQuantity === 0) return this.#add(cart.item_count);
    if (!qualifies && giftQuantity > 0) return this.#setGiftQuantity(giftLines, 0);
    if (qualifies && giftQuantity > 1) return this.#setGiftQuantity(giftLines, 1);
  }

  /**
   * @param {number} itemCount - Item count before the gift is added
   */
  async #add(itemCount) {
    if (!this.#allowAction()) return;

    const sections = cartSectionIds();
    const body = JSON.stringify({
      items: [{ id: config.giftVariantId, quantity: 1, properties: { _gift: 'auto' } }],
      ...(sections.length && { sections: sections.join(','), sections_url: window.location.pathname }),
    });

    const response = await fetch(Theme.routes.cart_add_url, fetchConfig('json', { body }));
    const data = await response.json();

    if (!response.ok || data.status) {
      // Gift sold out or unpublished: stop trying on this page
      this.#disabled = true;
      return;
    }

    this.#announce(itemCount + 1, data.sections);
  }

  /**
   * Keeps the first gift line at `quantity` and removes any other one.
   * @param {any[]} giftLines
   * @param {number} quantity
   */
  async #setGiftQuantity(giftLines, quantity) {
    if (!this.#allowAction()) return;

    /** @type {Record<string, number>} */
    const updates = {};
    giftLines.forEach((line, index) => {
      updates[line.key] = index === 0 ? quantity : 0;
    });

    const sections = cartSectionIds();
    const body = JSON.stringify({
      updates,
      ...(sections.length && { sections: sections.join(','), sections_url: window.location.pathname }),
    });

    const response = await fetch(`${Theme.routes.cart_update_url}.js`, fetchConfig('json', { body }));
    const data = await response.json();

    if (!response.ok || data.status) return;

    this.#announce(data.item_count, data.sections);
  }

  /**
   * Lets the drawer, the cart page and the cart icon re-render. Tagged so this script ignores its own events.
   * @param {number} itemCount
   * @param {Record<string, string>} [sections]
   */
  #announce(itemCount, sections) {
    document.dispatchEvent(new CartAddEvent({}, SOURCE, { source: SOURCE, itemCount, sections }));
  }

  /**
   * Safety net against an add/remove ping-pong (e.g. a discount that flips the total around the threshold).
   * @returns {boolean}
   */
  #allowAction() {
    const now = Date.now();
    this.#actions = this.#actions.filter((time) => now - time < ACTION_WINDOW);

    if (this.#actions.length >= MAX_ACTIONS) {
      this.#disabled = true;
      console.warn('[cart-rewards] Too many gift updates, auto gift paused on this page.');
      return false;
    }

    this.#actions.push(now);
    return true;
  }
}

if (config.giftEnabled && config.giftVariantId && config.giftProductId) {
  const giftSync = new GiftSync();

  document.addEventListener(ThemeEvents.cartUpdate, (event) => {
    if (/** @type {CustomEvent} */ (event).detail?.data?.source === SOURCE) return;
    giftSync.schedule();
  });
  document.addEventListener(ThemeEvents.discountUpdate, giftSync.schedule);
  window.addEventListener('pageshow', (event) => {
    if (event.persisted) giftSync.schedule();
  });

  if (config.sync) giftSync.schedule();
}

/**
 * Escapes text read from data attributes before it goes into innerHTML.
 * @param {string} value
 */
function escapeHtml(value) {
  const element = document.createElement('span');
  element.textContent = value;
  return element.innerHTML;
}

/**
 * @param {number} cents - Presentment currency cents
 * @param {boolean} [trimZeros]
 */
function formatMoney(cents, trimZeros = false) {
  const currency = window.Shopify?.currency?.active || config.shopCurrency || 'EUR';
  const whole = trimZeros && cents % 100 === 0;

  return new Intl.NumberFormat(document.documentElement.lang || 'fr', {
    style: 'currency',
    currency,
    minimumFractionDigits: whole ? 0 : 2,
    maximumFractionDigits: whole ? 0 : 2,
  }).format(cents / 100);
}

/**
 * Progress bar of the cart drawer. Rendered by snippets/cart-rewards.liquid; only re-renders client side
 * when the presentment currency differs from the shop currency. Mirrors the Liquid computation.
 */
class CartRewards extends HTMLElement {
  #observer = new MutationObserver(() => this.#sync());

  connectedCallback() {
    if (currencyRate(this.dataset.shopCurrency) === 1) return;

    this.#sync();
    // Morph drops data-converted when it restores the server markup: re-render then
    this.#observer.observe(this, { attributes: true, attributeFilter: ['data-converted'] });
  }

  disconnectedCallback() {
    this.#observer.disconnect();
  }

  #sync() {
    if (this.hasAttribute('data-converted')) return;

    this.#render(currencyRate(this.dataset.shopCurrency));
    this.setAttribute('data-converted', '');
  }

  /**
   * @param {number} rate
   */
  #render(rate) {
    const eligible = Number(this.dataset.eligible) || 0;
    const subUnlocks = this.dataset.subUnlocks === 'true';
    const tiers = [...this.querySelectorAll('.cart-rewards__milestone')].map((element) => {
      const milestone = /** @type {HTMLElement} */ (element);
      const threshold = Math.round(Number(milestone.dataset.threshold) * rate);
      const bySubscription = milestone.dataset.type === 'shipping' && subUnlocks;

      return {
        element: milestone,
        type: milestone.dataset.type,
        threshold,
        bySubscription,
        unlocked: bySubscription || eligible >= threshold,
        position: Number(milestone.dataset.position) || 0,
        goal: milestone.dataset.goal || '',
      };
    });

    let fill = 100;
    let previousPosition = 0;
    let previousThreshold = 0;
    /** @type {typeof tiers[number] | undefined} */
    let next;

    for (const tier of tiers) {
      if (tier.unlocked) {
        if (!tier.bySubscription) previousThreshold = tier.threshold;
        previousPosition = tier.position;
        continue;
      }

      next = tier;
      const span = Math.max(tier.threshold - previousThreshold, 1);
      const progress = Math.min(Math.max(eligible - previousThreshold, 0), span);
      fill = previousPosition + (progress * (tier.position - previousPosition)) / span;
      break;
    }

    let message;

    if (!next) {
      message = escapeHtml(this.dataset.success || '');
    } else {
      message = escapeHtml(next.goal).replace('[amount]', `<strong>${formatMoney(next.threshold - eligible)}</strong>`);
    }

    const messageElement = this.querySelector('.cart-rewards__message');
    if (messageElement) messageElement.innerHTML = message;

    this.style.setProperty('--cr-fill', `${fill.toFixed(2)}%`);

    const track = this.querySelector('.cart-rewards__track');
    track?.setAttribute('aria-valuenow', String(Math.round(fill)));
    track?.setAttribute('aria-valuetext', messageElement?.textContent || '');

    for (const tier of tiers) {
      tier.element.classList.toggle('is-unlocked', tier.unlocked);
      const amount = tier.element.querySelector('.cart-rewards__amount');
      if (amount) amount.textContent = formatMoney(tier.threshold, true);
    }
  }
}

if (!customElements.get('cart-rewards')) {
  customElements.define('cart-rewards', CartRewards);
}
